import React, { useEffect, useState } from "react";
import { Send, Bell, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

function NumberField({ label, value, unit, onChange, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] block mb-1.5">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={testid}
          className="flex-1 bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#10B981]"
        />
        {unit && (
          <span className="font-mono text-xs text-[#A1A1AA] w-12">{unit}</span>
        )}
      </div>
    </label>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [tg, setTg] = useState({ bot_token: "", chat_id: "", enabled: true });
  const [alerts, setAlerts] = useState({
    temp_threshold: 75,
    temp_critical: 82,
    hashrate_drop_pct: 30,
    offline_minutes: 3,
    enabled: true,
  });
  const [saving, setSaving] = useState({ tg: false, alerts: false, test: false, fleet: false });

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setTg(data.telegram || { bot_token: "", chat_id: "", enabled: true });
    setAlerts(data.alerts || alerts);
  };
  useEffect(() => { load(); }, []);

  const saveTelegram = async () => {
    setSaving((s) => ({ ...s, tg: true }));
    try {
      await api.put("/settings/telegram", tg);
      toast.success("Settings Telegram salvate");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving((s) => ({ ...s, tg: false }));
    }
  };

  const saveAlerts = async () => {
    setSaving((s) => ({ ...s, alerts: true }));
    try {
      await api.put("/settings/alerts", alerts);
      toast.success("Threshold alert salvati");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving((s) => ({ ...s, alerts: false }));
    }
  };

  const testTelegram = async () => {
    setSaving((s) => ({ ...s, test: true }));
    try {
      await api.post("/settings/telegram/test");
      toast.success("Messaggio di test inviato — controlla Telegram!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving((s) => ({ ...s, test: false }));
    }
  };

  const sendFleetStatus = async () => {
    setSaving((s) => ({ ...s, fleet: true }));
    try {
      await api.post("/settings/telegram/fleet-status");
      toast.success("Stato flotta inviato su Telegram");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving((s) => ({ ...s, fleet: false }));
    }
  };

  if (!settings) {
    return <div className="p-8 font-mono text-sm text-[#A1A1AA]">Loading…</div>;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-3xl">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10B981] mb-1.5">
          // System Configuration
        </div>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
          Settings
        </h1>
        <p className="text-sm text-[#A1A1AA] mt-1">
          Configura Telegram, soglie di alert e gestione token.
        </p>
      </div>

      {/* Telegram */}
      <div className="bento p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Send className="w-4 h-4 text-[#10B981]" />
          <h3 className="font-heading font-bold text-lg">Telegram Alerts</h3>
          <label className="ml-auto flex items-center gap-2 text-xs font-mono text-[#A1A1AA]">
            <input
              type="checkbox"
              checked={tg.enabled}
              onChange={(e) => setTg({ ...tg, enabled: e.target.checked })}
              data-testid="tg-enabled"
              className="accent-[#10B981]"
            />
            <span>{tg.enabled ? "ATTIVO" : "DISATTIVATO"}</span>
          </label>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] block mb-1.5">
              Bot Token
            </span>
            <input
              type="text"
              value={tg.bot_token}
              onChange={(e) => setTg({ ...tg, bot_token: e.target.value })}
              placeholder="12345:ABC..."
              data-testid="tg-token-input"
              className="w-full bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#10B981]"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] block mb-1.5">
              Chat ID
            </span>
            <input
              type="text"
              value={tg.chat_id}
              onChange={(e) => setTg({ ...tg, chat_id: e.target.value })}
              placeholder="1234567890"
              data-testid="tg-chat-input"
              className="w-full bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#10B981]"
            />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 flex-wrap">
          <button
            onClick={sendFleetStatus}
            disabled={saving.fleet}
            data-testid="tg-fleet-status-btn"
            className="bg-[#10B981]/10 hover:bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40 rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em] font-mono"
            title="Invia un riepilogo della flotta (online/offline, hashrate, power) al tuo Telegram"
          >
            {saving.fleet ? "Invio…" : "↗ Invia stato flotta"}
          </button>
          <button
            onClick={testTelegram}
            disabled={saving.test}
            data-testid="tg-test-btn"
            className="bg-[#27272A] hover:bg-[#3F3F46] text-[#FAFAFA] border border-[#3F3F46] rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em] font-mono"
          >
            {saving.test ? "Invio…" : "Invia test"}
          </button>
          <button
            onClick={saveTelegram}
            disabled={saving.tg}
            data-testid="tg-save-btn"
            className="bg-[#10B981] hover:bg-[#34D399] disabled:opacity-50 text-[#09090B] font-bold rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em] flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" /> {saving.tg ? "Salvo…" : "Salva"}
          </button>
        </div>

        <div className="mt-4 bg-[#0B0B0F] border border-[#27272A] rounded-sm p-3 text-[10px] font-mono leading-relaxed text-[#A1A1AA]" data-testid="tg-commands-info">
          <div className="text-[#10B981] uppercase tracking-[0.15em] mb-1">💬 comandi via Telegram</div>
          Puoi anche scrivere direttamente al bot:
          <span className="text-[#FAFAFA]"> /status </span>
          (riepilogo flotta) ·
          <span className="text-[#FAFAFA]"> /help </span>
          (aiuto)
        </div>
      </div>

      {/* Alert thresholds */}
      <div className="bento p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-4 h-4 text-[#10B981]" />
          <h3 className="font-heading font-bold text-lg">Alert Thresholds</h3>
          <label className="ml-auto flex items-center gap-2 text-xs font-mono text-[#A1A1AA]">
            <input
              type="checkbox"
              checked={alerts.enabled}
              onChange={(e) => setAlerts({ ...alerts, enabled: e.target.checked })}
              data-testid="alerts-enabled"
              className="accent-[#10B981]"
            />
            <span>{alerts.enabled ? "ATTIVO" : "DISATTIVATO"}</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumberField
            label="Temp warning (>=)"
            value={alerts.temp_threshold}
            unit="°C"
            onChange={(v) => setAlerts({ ...alerts, temp_threshold: v })}
            testid="alerts-temp-threshold"
          />
          <NumberField
            label="Temp critical (>=)"
            value={alerts.temp_critical}
            unit="°C"
            onChange={(v) => setAlerts({ ...alerts, temp_critical: v })}
            testid="alerts-temp-critical"
          />
          <NumberField
            label="Hashrate drop"
            value={alerts.hashrate_drop_pct}
            unit="%"
            onChange={(v) => setAlerts({ ...alerts, hashrate_drop_pct: v })}
            testid="alerts-hashrate-drop"
          />
          <NumberField
            label="Offline timeout"
            value={alerts.offline_minutes}
            unit="min"
            onChange={(v) => setAlerts({ ...alerts, offline_minutes: v })}
            testid="alerts-offline-minutes"
          />
        </div>

        <div className="flex items-center justify-end mt-5">
          <button
            onClick={saveAlerts}
            disabled={saving.alerts}
            data-testid="alerts-save-btn"
            className="bg-[#10B981] hover:bg-[#34D399] disabled:opacity-50 text-[#09090B] font-bold rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em] flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" /> {saving.alerts ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>

      {/* Account info */}
      <div className="bento p-5">
        <h3 className="font-heading font-bold text-lg mb-3">Account</h3>
        <div className="font-mono text-xs text-[#A1A1AA] space-y-1">
          <div>Login admin con username/password (impostati in <span className="text-[#FAFAFA]">backend/.env</span>).</div>
          <div>Cambio password: modifica <span className="text-[#FAFAFA]">ADMIN_PASSWORD</span> in .env e riavvia il backend.</div>
        </div>
      </div>
    </div>
  );
}
